/**
 * Sticky Cart Module - 優化版
 * 處理購物車的即時更新、顯示/隱藏、數量調整等功能
 */

// 狀態管理和常量
let isCartLoading = false;
let cartTaskActive = false;
let cartTask = Promise.resolve();
let refreshPromise = null;
let refreshRequested = false;
let quantityPending = false;
let cartNeedsReconcile = false;
let cartAddUncertainty = null;
let cartClearPending = false;
let cartClearUncertainty = null;
let cartAddsPending = 0;
let savedBody = null;
let cartOpener = null;
let closeTimer;
let cartFocus = null;
let lastDeviceType = null; // 追蹤上一次的設備類型
const BREAKPOINTS = {
  mobile: 780,
  tablet: 1440
};

// 工具函數
const isMobile = () => window.innerWidth <= BREAKPOINTS.mobile;
const isTablet = () => window.innerWidth > BREAKPOINTS.mobile && window.innerWidth < BREAKPOINTS.tablet;
const isDesktop = () => window.innerWidth >= BREAKPOINTS.tablet;
const getDevice = () => isDesktop() ? 'desktop' : (isTablet() ? 'tablet' : 'mobile');

// DOM 選擇器緩存
const getElements = () => ({
  cart: document.querySelector(".sticky-cart"),
  wrapper: document.querySelector(".sticky-cart-wrapper"),
  button: document.getElementById("open-cart-button"),
  closeButton: document.getElementById("close-cart-button"),
  cartBox: document.querySelector(".sticky-cart-box"),
  productWrapper: document.querySelector('.collection-product-wrapper'),
  header: document.querySelector('header') || document.querySelector('.header'),
  loadingEl: document.querySelector(".cart-loading-animation")
});

/**
 * 檢查設備類型變化並調整顯示狀態
 */
function checkDeviceTypeChange() {
  const currentDevice = getDevice();
  
  // 如果設備類型沒有改變，不需要做任何事
  if (lastDeviceType === currentDevice) {
    return;
  }
  
  const { cart, button, wrapper } = getElements();
  if (!cart || !button || !wrapper) {
    return;
  }
  const hadFocus = cart.contains(document.activeElement);
  clearTimeout(closeTimer);
  unlockCartPage();
  cart.removeAttribute('aria-modal');
  cart.setAttribute('role', 'region');
  button.setAttribute('aria-expanded', 'false');
  cart.inert = currentDevice !== 'desktop';
  
  // 根據新的設備類型重新設置顯示狀態
  if (currentDevice === 'desktop') {
    wrapper.style.display = "block";
    cart.style.display = "flex";
    button.style.display = "none";
    wrapper.classList.remove("show");
  } else if (currentDevice === 'tablet') {
    wrapper.style.display = "block";
    cart.style.display = "none";
    wrapper.classList.remove("show");
    // 確保按鈕顯示並應用正確樣式
    button.style.display = "block";
    button.style.visibility = "visible";
    button.style.opacity = "1";
    applyTabletButtonStyles(button);
  } else if (currentDevice === 'mobile') {
    wrapper.style.display = "block";
    cart.style.display = "none";
    wrapper.classList.remove("show");
    // 確保按鈕顯示並應用正確樣式
    button.style.display = "block";
    button.style.visibility = "visible";
    button.style.opacity = "1";
    applyMobileButtonStyles(button);
  }
  
  // 更新上一次的設備類型
  lastDeviceType = currentDevice;
  if (hadFocus) (currentDevice === 'desktop' ? cart : button).focus({ preventScroll: true });
}
function toggleCartLoading(show) {
  isCartLoading = show || cartTaskActive || quantityPending;
  const { loadingEl, cart } = getElements();
  if (loadingEl) loadingEl.style.display = isCartLoading ? "block" : "none";
  if (cart) {
    cart.setAttribute('aria-busy', String(isCartLoading));
    cart.querySelectorAll('[data-cart-action], [data-cart-clear], [name="checkout"]').forEach(element => {
      element.disabled = isCartLoading || cartNeedsReconcile;
    });
  }
}

function cartMessage(message, error = false) {
  const { cart } = getElements();
  const status = cart?.querySelector(error ? '.sticky-cart-error' : '.sticky-cart-status');
  if (status) status.textContent = message;
}

function reportCartError(error) {
  console.error('Mini cart:', error);
  cartMessage(error.message, true);
}

function enqueueCartTask(task) {
  const next = cartTask.then(async () => {
    if (!cartFocus) cartFocus = captureCartFocus();
    cartTaskActive = true;
    toggleCartLoading(true);
    try {
      return await task();
    } finally {
      cartTaskActive = false;
      toggleCartLoading(false);
      restoreCartFocus(cartFocus);
      if (!quantityPending) cartFocus = null;
    }
  });
  // Keep the queue usable; callers still receive and report the original rejection.
  cartTask = next.catch(() => {});
  return next;
}

async function requestCart(url, options = {}, format = 'text') {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);
  try {
    const res = await fetch(url, { ...options, signal: controller.signal });
    const data = await (format === 'json' ? res.json() : res.text());
    if (!res.ok) {
      const error = new Error((format === 'json' && (data.description || data.message))
        || `購物車請求失敗（HTTP ${res.status}），請重試。`);
      error.rejected = res.status >= 400 && res.status < 500 && res.status !== 408;
      throw error;
    }
    return data;
  } catch (error) {
    if (controller.signal.aborted) throw new Error('購物車請求逾時。');
    if (error instanceof TypeError) throw new Error('無法連線至購物車，請檢查網絡。');
    if (error instanceof SyntaxError) {
      console.error('Invalid cart response:', error);
      throw new Error('購物車回應格式不正確。');
    }
    throw error;
  } finally {
    clearTimeout(timeout);
  }
}

async function updateCartSummary() {
  const root = getElements().wrapper?.dataset.rootUrl || window.Shopify?.routes?.root || '/';
  const cart = await requestCart(`${root}cart.js`, { cache: 'no-store' }, 'json');
  if (!Number.isSafeInteger(cart.item_count) || !Array.isArray(cart.items)) {
    throw new Error('購物車回應不完整，請重新載入頁面。');
  }
  updateCartItemCounts(cart.item_count);
  return cart;
}

async function addCartItem(url, options) {
  if (cartAddUncertainty) throw cartAddUncertainty;
  if (cartClearUncertainty) throw cartClearUncertainty;
  if (cartClearPending) throw new Error('正在清空購物車，請稍候。');
  cartAddsPending++;
  try {
    const item = await requestCart(url, options, 'json');
    if (!item || !Number.isSafeInteger(item.id) || item.id < 1) {
      throw new Error('加入購物車回應不完整。');
    }
    return item;
  } catch (error) {
    if (error.rejected) throw error;
    const uncertain = new Error('加入結果未明，正在核對購物車。請勿重複加入，並先重新載入頁面核對。');
    uncertain.cartUncertain = true;
    cartAddUncertainty = uncertain;
    let confirmed = false;
    try {
      await updateCartSummary();
      if (getElements().cart && await updateCartDrawer() === false) {
        throw new Error('未能更新購物車畫面。');
      }
      confirmed = true;
    } catch (readError) {
      console.error('Unable to reconcile cart add:', readError);
    }
    uncertain.message = `${error.message} ${confirmed ? '已讀取最新購物車，但未能確認此次加入結果。' : '未能讀取最新購物車。'}請先重新載入頁面，再開啟購物車核對；請勿重複加入。`;
    if (getElements().cart) {
      cartNeedsReconcile = !confirmed;
      cartMessage(uncertain.message, true);
      toggleCartLoading(false);
    }
    throw uncertain;
  } finally {
    cartAddsPending--;
  }
}

async function clearCart(url) {
  if (cartClearUncertainty) throw cartClearUncertainty;
  if (cartClearPending || cartAddsPending || quantityPending || isCartLoading) {
    throw new Error('購物車正在更新，請稍候再清空。');
  }
  cartClearPending = true;
  quantityPending = true;
  toggleCartLoading(true);
  try {
    await enqueueCartTask(async () => {
      let cart;
      let mutationError;
      try {
        cart = await requestCart(url, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: '{}',
        }, 'json');
        if (cart?.item_count !== 0 || !Array.isArray(cart.items) || cart.items.length !== 0) {
          throw new Error('清空購物車回應不完整。');
        }
      } catch (error) {
        if (error.rejected) throw error;
        mutationError = error;
        cart = await updateCartSummary().catch(readError => {
          console.error('Unable to reconcile cart clear:', readError);
          return null;
        });
      }
      if (!cart || cart.item_count !== 0 || !Array.isArray(cart.items) || cart.items.length !== 0) {
        const error = new Error(`${mutationError?.message || '未能確認購物車狀態。'} 未能確認已清空，請重新載入頁面核對；不會自動重送清空請求。`);
        error.cartUncertain = true;
        cartClearUncertainty = error;
        cartNeedsReconcile = true;
        throw error;
      }
      updateCartItemCounts(0);
      cartNeedsReconcile = true;
    });
  } finally {
    cartClearPending = false;
    quantityPending = false;
    toggleCartLoading(false);
  }
}

/**
 * 更新購物車計數顯示
 */
function updateCartItemCounts(count) {
  document.querySelectorAll(".cart-count").forEach(el => el.textContent = count);
  document.dispatchEvent(new Event("cart:update"));
}

/**
 * 獲取購物車內容
 */
async function fetchCartContent() {
  const root = getElements().wrapper?.dataset.rootUrl || window.Shopify?.routes?.root || '/';
  return requestCart(`${root}?section_id=r-sticky-cart`, { cache: 'no-store' });
}

/**
 * 更新購物車內容
 */
function updateCartDrawer() {
  if (!getElements().cart) return Promise.resolve();
  refreshRequested = true;
  if (refreshPromise) return refreshPromise;
  refreshPromise = enqueueCartTask(async () => {
    do {
      refreshRequested = false;
      await updateCartContent(await fetchCartContent());
    } while (refreshRequested);
    cartNeedsReconcile = false;
    cartMessage('', true);
  }).catch(error => {
    cartNeedsReconcile = true;
    reportCartError(error);
    return false;
  }).finally(() => {
    refreshPromise = null;
    toggleCartLoading(false);
  });
  return refreshPromise;
}

/**
 * 更新購物車 DOM 內容
 */
async function updateCartContent(htmlText) {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlText;
  
  const newCart = tempDiv.querySelector(".sticky-cart");
  const { cart: currentCart } = getElements();
  
  const count = Number(newCart?.dataset.itemCount);
  if (!newCart || !currentCart || !Number.isSafeInteger(count) || count < 0) {
    throw new Error('購物車回應不完整，請重新整理購物車。');
  }
  const list = currentCart.querySelector('.sticky-cart-items');
  const scrollTop = list?.scrollTop || 0;
  const outerScroll = currentCart.querySelector('.sticky-cart-scrollbar')?.scrollTop || 0;
  const focus = cartFocus || captureCartFocus();
  stopScrollEnhancements();
  currentCart.innerHTML = newCart.innerHTML;
  currentCart.dataset.itemCount = String(count);
  toggleCartLoading(isCartLoading);
  const newList = currentCart.querySelector('.sticky-cart-items');
  if (newList) newList.scrollTop = scrollTop;
  const newOuter = currentCart.querySelector('.sticky-cart-scrollbar');
  if (newOuter) newOuter.scrollTop = outerScroll;
  initializeStickyCartListeners();
  restoreCartFocus(focus);
  startScrollEnhancements();
  updateCartItemCounts(count);
}

function captureCartFocus() {
  const active = document.activeElement;
  const { cart } = getElements();
  if (!cart?.contains(active)) return null;
  const item = active.closest('.sticky-cart-item');
  return { key: item?.dataset.lineItemKey, action: active.dataset.cartAction, id: active.id };
}

function restoreCartFocus(focus) {
  if (!focus) return;
  const { cart } = getElements();
  const item = [...cart.querySelectorAll('.sticky-cart-item')].find(row => row.dataset.lineItemKey === focus.key);
  const target = (focus.action && item?.querySelector(`[data-cart-action="${focus.action}"]`))
    || (focus.id && cart.querySelector(`#${focus.id}`))
    || cart.querySelector('#close-cart-button');
  if (target && !target.disabled) target.focus({ preventScroll: true });
}

async function changeCartQuantity(button) {
  if (quantityPending || isCartLoading || cartNeedsReconcile) return;
  const item = button.closest('.sticky-cart-item');
  const currentQty = Number(item.querySelector('input').value);
  const quantity = button.dataset.cartAction === 'remove' ? 0
    : currentQty + (button.dataset.cartAction === 'increase' ? 1 : -1);
  if (!Number.isSafeInteger(quantity) || quantity < 0) {
    reportCartError(new Error('商品數量無效，請重新整理購物車。'));
    return;
  }
  quantityPending = true;
  cartFocus = captureCartFocus();
  toggleCartLoading(true);
  cartMessage('', true);
  cartMessage('正在更新購物車…');
  try {
    await enqueueCartTask(async () => {
      const { wrapper } = getElements();
      const data = await requestCart(wrapper.dataset.changeUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: item.dataset.lineItemKey, quantity,
          sections: ['r-sticky-cart'], sections_url: wrapper.dataset.rootUrl,
        }),
      }, 'json');
      const html = data.sections?.['r-sticky-cart'];
      await updateCartContent(typeof html === 'string' ? html : await fetchCartContent());
      cartNeedsReconcile = false;
      cartMessage('購物車已更新。');
    });
  } catch (error) {
    // A failed response may follow an applied change. Read back; never replay the POST.
    cartNeedsReconcile = true;
    try {
      await enqueueCartTask(async () => {
        await updateCartContent(await fetchCartContent());
        cartNeedsReconcile = false;
      });
    } catch (readError) {
      reportCartError(readError);
    }
    cartMessage('');
    reportCartError(new Error(`${error.message} ${cartNeedsReconcile ? '未能確認最新數量，請按「重新整理購物車」後再繼續。' : '已重新讀取最新購物車。'}`));
  } finally {
    quantityPending = false;
    toggleCartLoading(false);
    restoreCartFocus(cartFocus);
    cartFocus = null;
  }
}

function addQuantityListeners() {
  const { cart } = getElements();
  if (!cart || cart.dataset.actionsBound) return;
  cart.dataset.actionsBound = 'true';
  cart.addEventListener('click', event => {
    const button = event.target.closest('[data-cart-action]');
    if (button && !button.disabled) changeCartQuantity(button);
    if (event.target.closest('[data-cart-refresh]')) updateCartDrawer();
  });
  cart.addEventListener('submit', event => {
    if (isCartLoading || quantityPending || cartNeedsReconcile) {
      event.preventDefault();
      cartMessage('請等待購物車更新，或先重新整理購物車。', true);
    }
  });
}

/**
 * 購物車顯示/隱藏控制
 */
function toggleCartVisibility(show, device = 'mobile') {
  const { cart, wrapper, button } = getElements();
  if (!cart || !wrapper || !button) return;
  clearTimeout(closeTimer);
  if (show) {
    if (device === 'desktop') return;
    if (!wrapper.classList.contains('show')) cartOpener = document.activeElement;
    wrapper.style.display = 'block';
    cart.style.display = 'flex';
    cart.inert = false;
    cart.setAttribute('role', 'dialog');
    cart.setAttribute('aria-modal', 'true');
    wrapper.classList.add('show');
    button.style.display = 'none';
    button.setAttribute('aria-expanded', 'true');
    lockCartPage();
    getElements().closeButton?.focus({ preventScroll: true });
  } else {
    wrapper.classList.remove('show');
    cart.inert = true;
    cart.removeAttribute('aria-modal');
    cart.setAttribute('role', 'region');
    button.style.display = 'block';
    button.style.visibility = 'visible';
    button.style.opacity = '1';
    button.setAttribute('aria-expanded', 'false');
    unlockCartPage();
    const opener = cartOpener?.isConnected && cartOpener.getClientRects().length ? cartOpener : button;
    opener.focus({ preventScroll: true });
    closeTimer = setTimeout(() => {
      if (!wrapper.classList.contains('show') && !isDesktop()) cart.style.display = 'none';
    }, 300);
  }
}

function lockCartPage() {
  if (savedBody) return;
  const { style } = document.body;
  savedBody = { y: window.scrollY, overflow: style.overflow, position: style.position, width: style.width, top: style.top };
  Object.assign(style, { overflow: 'hidden', position: 'fixed', width: '100%', top: `-${savedBody.y}px` });
}

function unlockCartPage() {
  if (!savedBody) return;
  const { y, ...styles } = savedBody;
  savedBody = null;
  Object.assign(document.body.style, styles);
  window.scrollTo({ top: y, behavior: 'instant' });
}

/**
 * 按鈕事件監聽器
 */
function attachButtonListeners() {
  const { button: openBtn, closeButton: closeBtn, productWrapper } = getElements();
  if (!openBtn || !closeBtn) return;
  
  // 移除舊的事件監聽器（如果有的話）
  const clickHandler = openBtn._clickHandler;
  if (clickHandler) {
    openBtn.removeEventListener('click', clickHandler);
  }
  
  // 確保按鈕在手機/平板版可見
  ensureButtonVisible();
  
  // 開啟購物車
  const newClickHandler = () => {
    if (isDesktop()) {
      // 桌面版：滾動到產品區域
      if (productWrapper) {
        productWrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } else {
      toggleCartVisibility(true, getDevice());
    }
  };
  
  openBtn.addEventListener("click", newClickHandler);
  openBtn._clickHandler = newClickHandler;

  // 關閉購物車（只有非桌面版）
  closeBtn.onclick = () => {
    if (!isDesktop()) {
      toggleCartVisibility(false, getDevice());
      // 關閉後確保按鈕仍然可見
      setTimeout(() => {
        ensureButtonVisible();
      }, 350);
    }
  };
}

/**
 * 應用平板版按鈕樣式
 */
function applyTabletButtonStyles(button) {
  if (!button) return;
  
  const { wrapper } = getElements();
  const isCartOpen = wrapper && wrapper.classList.contains('show');
  
  // 確保有正確的類別
  if (!button.classList.contains('open-cart-button')) {
    button.classList.add('open-cart-button');
  }
  
  // 只在購物車關閉時設置顯示樣式
  if (!isCartOpen) {
    button.style.display = 'block';
    button.style.visibility = 'visible';
    button.style.opacity = '1';
  }
  
  button.style.pointerEvents = 'auto';
}

/**
 * 應用手機版按鈕樣式
 */
function applyMobileButtonStyles(button) {
  if (!button) return;
  
  const { wrapper } = getElements();
  const isCartOpen = wrapper && wrapper.classList.contains('show');
  
  // 確保有正確的類別
  if (!button.classList.contains('open-cart-button')) {
    button.classList.add('open-cart-button');
  }
  
  // 只在購物車關閉時設置顯示樣式
  if (!isCartOpen) {
    button.style.display = 'flex'; // 手機版使用 flex 來居中對齊
    button.style.visibility = 'visible';
    button.style.opacity = '1';
  }
  
  button.style.pointerEvents = 'auto';
}

/**
 * 外部點擊關閉購物車
 */
function addOutsideClickListeners() {
  if (eventListenersAdded.outside) return;
  
  const handleOutsideClick = (event) => {
    if (isDesktop()) return;
    
    const { cart, button, wrapper } = getElements();
    
    if (wrapper?.classList.contains('show') &&
        !cart.contains(event.target) && 
        !button?.contains(event.target)) {
      
      toggleCartVisibility(false, getDevice());
      // 外部點擊關閉後確保按鈕仍然可見
      setTimeout(() => {
        ensureButtonVisible();
      }, 350);
    }
  };

  ['click', 'touchstart'].forEach(eventType => {
    document.addEventListener(eventType, handleOutsideClick, { passive: true });
  });
  
  eventListenersAdded.outside = true;
}

/**
 * 觸控事件處理（防止冒泡）+ 拖動條關閉手勢
 */
function addTouchListeners() {
  const { wrapper, cart } = getElements();
  if (!wrapper || !cart || cart.dataset.touchBound) return;
  cart.dataset.touchBound = 'true';

  let startY = 0;
  let currentY = 0;
  let isDragging = false;
  let startedInDragZone = false;

  // 拖動條關閉手勢（僅限手機版）
  {
    const handleTouchStart = (e) => {
      if (!isMobile() || !wrapper.classList.contains('show')) return;
      if (!cart.contains(e.target)) return;
      
      // 檢查是否點擊在拖動條區域（購物車頂部 40px 區域）
      const cartRect = cart.getBoundingClientRect();
      const relativeY = e.touches[0].clientY - cartRect.top;
      
      startY = e.touches[0].clientY;
      currentY = startY;
      isDragging = false;
      
      // 標記是否從拖動區域開始
      startedInDragZone = relativeY <= 40;
    };

    const handleTouchMove = (e) => {
      if (startY === 0) return;
      
      // 如果不是從拖動區域開始的觸摸，完全不處理
      if (!startedInDragZone) {
        return;
      }
      
      currentY = e.touches[0].clientY;
      const deltaY = currentY - startY;
      
      // 只處理向下拖動的情況，且必須從拖動區域開始
      if (deltaY > 10) {
        isDragging = true;
        e.preventDefault();
        
        // 視覺反饋：輕微向下移動購物車
        const translateY = Math.min(deltaY * 0.5, 50);
        cart.style.transform = `translateY(${translateY}px)`;
        cart.style.opacity = Math.max(1 - deltaY / 200, 0.7);
      }
    };

    const handleTouchEnd = (e) => {
      const deltaY = currentY - startY;
      
      if (isDragging && startedInDragZone) {
        // 重置下滑關閉的樣式
        cart.style.transform = '';
        cart.style.opacity = '';
        
        // 如果下滑距離超過 80px，關閉購物車
        if (deltaY > 80) {
          toggleCartVisibility(false, 'mobile');
          // 確保關閉後按鈕仍可見
          setTimeout(() => {
            ensureButtonVisible();
          }, 350);
        }
        
        isDragging = false;
      }
      
      startY = 0;
      startedInDragZone = false;
    };

    // 只在購物車本身添加觸控事件，不在 wrapper 上添加
    cart.addEventListener('touchstart', handleTouchStart, { passive: false });
    cart.addEventListener('touchmove', handleTouchMove, { passive: false });
    cart.addEventListener('touchend', handleTouchEnd, { passive: true });
  }
}

/**
 * 桌面版位置更新
 */
function updateDesktopPosition(wrapper, button) {
  // 嚴格確保只在桌面版執行，避免影響其他設備
  if (!isDesktop()) {
    return;
  }
  
  const { productWrapper, header, cart } = getElements();
  if (!productWrapper) return;

  const wrapperRect = productWrapper.getBoundingClientRect();
  const windowHeight = window.innerHeight;
  const { top: wrapperTop, bottom: wrapperBottom } = wrapperRect;
  
  const isWrapperVisible = wrapperBottom > 0 && wrapperTop < windowHeight;

  if (isWrapperVisible) {
    cart.style.display = 'flex';
    // 設置基本樣式
    Object.assign(wrapper.style, {
      display: 'block',
      height: 'auto',
      maxHeight: 'none',
      overflow: 'visible',
      transform: 'none'
    });
    button.style.display = 'none';
    
    const actualHeight = wrapper.offsetHeight;
    const headerHeight = header ? header.offsetHeight : 0;
    const minAllowedTop = wrapperTop + 20;
    const maxAllowedTop = wrapperBottom - actualHeight - 20;
    const centerTop = Math.max(headerHeight + 10, (windowHeight - actualHeight) / 2);
    const finalTop = Math.max(minAllowedTop, Math.min(centerTop, maxAllowedTop));
    wrapper.style.top = finalTop + 'px';
  } else {
    cart.style.display = 'none';
    wrapper.style.display = 'contents';
    button.style.display = 'block';
  }
}

/**
 * 移動版位置更新 - 簡化版本
 */
function updateMobilePosition(button) {
  // 嚴格確保只在手機版執行，避免影響其他設備
  if (!isMobile()) {
    return;
  }
  
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  const windowHeight = window.innerHeight;
  const documentHeight = document.documentElement.scrollHeight;
  const distanceFromBottom = documentHeight - scrollTop - windowHeight;
  
  let bottomOffset = 20;
  if (distanceFromBottom < 150) {
    bottomOffset = Math.max(20, 150 - distanceFromBottom + 20);
  }
  
  const { cart } = getElements();
  if (!cart || !getElements().wrapper?.classList.contains('show')) {
    button.style.bottom = bottomOffset + 'px';
    // 只設置位置，不覆蓋其他樣式
    applyMobileButtonStyles(button);
  }
}

/**
 * 強制確保手機/平板版按鈕可見（修復按鈕消失問題）
 */
function ensureButtonVisible() {
  const { button, wrapper } = getElements();
  if (!button || !wrapper) return;
  
  const currentDevice = getDevice();
  const isCartOpen = wrapper.classList.contains('show');
  
  // 只在購物車關閉時確保按鈕可見
  if (!isCartOpen && (currentDevice === 'mobile' || currentDevice === 'tablet')) {
    // 確保 wrapper 顯示（但可能不在螢幕上）
    wrapper.style.display = "block";
    
    // 應用對應的樣式（會設置所有必要的可見性屬性）
    if (currentDevice === 'mobile') {
      applyMobileButtonStyles(button);
    } else {
      applyTabletButtonStyles(button);
    }
  }
}

/**
 * 事件監聽器狀態管理
 */
let eventListenersAdded = {
  scroll: false,
  resize: false,
  orientationchange: false,
  outside: false
};

/**
 * 滾動跟隨功能
 */
function addScrollFollowListener() {
  if (eventListenersAdded.scroll) return;
  
  let isScrolling = false;
  let scrollTimeout;
  let resizeTimeout;

  function updateCartPosition() {
    const { wrapper: cartWrapper, button: openButton, cart } = getElements();
    if (!cartWrapper || !openButton || !cart) return;

    const currentDevice = getDevice();
    const isCartOpen = cartWrapper.classList.contains('show');
    
    // 只在購物車關閉時確保按鈕狀態正確
    if (!isCartOpen) {
      if (currentDevice === 'mobile') {
        // 手機版購物車關閉時確保按鈕可見
        applyMobileButtonStyles(openButton);
      } else if (currentDevice === 'tablet') {
        // 平板版購物車關閉時確保按鈕可見
        applyTabletButtonStyles(openButton);
      }
    }

    if (isDesktop()) {
      updateDesktopPosition(cartWrapper, openButton);
    } else if (isMobile()) {
      updateMobilePosition(openButton);
    }
    // 平板版不需要特殊的位置更新邏輯
  }

  function handleScroll() {
    if (!isScrolling) {
      isScrolling = true;
      requestAnimationFrame(() => {
        updateCartPosition();
        isScrolling = false;
      });
    }
    
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(updateCartPosition, 150);
  }

  function handleResize() {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      checkDeviceTypeChange();
      updateCartPosition();
    }, 200);
  }

  function handleOrientationChange() {
    checkDeviceTypeChange();
    updateCartPosition();
  }

  // 事件監聽器 - 統一管理
  if (!eventListenersAdded.scroll) {
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('load', updateCartPosition);
    eventListenersAdded.scroll = true;
  }
  
  if (!eventListenersAdded.resize) {
    window.addEventListener('resize', handleResize, { passive: true });
    eventListenersAdded.resize = true;
  }
  
  if (!eventListenersAdded.orientationchange) {
    window.addEventListener('orientationchange', handleOrientationChange, { passive: true });
    eventListenersAdded.orientationchange = true;
  }

  window.updateCartPosition = updateCartPosition;
  updateCartPosition();
}

/**
 * 初始化所有事件監聽器
 */
function initializeStickyCartListeners() {
  addQuantityListeners();
  attachButtonListeners();
}

function addCartKeyboardListeners() {
  if (eventListenersAdded.keyboard) return;
  eventListenersAdded.keyboard = true;
  document.addEventListener('keydown', event => {
    const { cart, wrapper } = getElements();
    if (isDesktop() || !wrapper?.classList.contains('show')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      toggleCartVisibility(false, getDevice());
    } else if (event.key === 'Tab') {
      const targets = [...cart.querySelectorAll('a[href], button, input, [tabindex]')]
        .filter(element => !element.disabled && element.tabIndex >= 0 && element.getClientRects().length);
      const first = targets[0];
      const last = targets[targets.length - 1];
      if (!first) {
        event.preventDefault();
        cart.focus({ preventScroll: true });
      } else if (event.shiftKey && (document.activeElement === first || !cart.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !cart.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    }
  });
  document.addEventListener('focusin', event => {
    const { cart, wrapper, closeButton } = getElements();
    if (!isDesktop() && wrapper?.classList.contains('show') && !cart.contains(event.target)) {
      closeButton?.focus({ preventScroll: true });
    }
  });
}

/**
 * 初始化 Sticky Cart 模組
 */
function initializeStickyCart() {
  const { cart, button, wrapper } = getElements();
  
  // 設置初始設備類型
  lastDeviceType = getDevice();
  if (cart) cart.inert = !isDesktop();
  
  // 完全由JavaScript控制顯示/隱藏狀態
  if (isDesktop() && cart && button && wrapper) {
    // 桌面版：顯示購物車和wrapper，隱藏按鈕
    wrapper.style.display = "block";
    cart.style.display = "flex";
    button.style.display = "none";
  } else if (isTablet() && cart && button && wrapper) {
    // 平板版：隱藏購物車，顯示按鈕，wrapper準備待命
    wrapper.style.display = "block";
    cart.style.display = "none";
    wrapper.classList.remove("show");
    button.style.display = "block";
    button.style.visibility = "visible";
    button.style.opacity = "1";
    
    // 應用平板版專用樣式
    applyTabletButtonStyles(button);
  } else if (isMobile() && cart && button && wrapper) {
    // 手機版：顯示 wrapper（讓按鈕可見），隱藏購物車，顯示按鈕
    wrapper.style.display = "block";  // 🔧 修正：手機版 wrapper 必須 display: block
    cart.style.display = "none";
    wrapper.classList.remove("show");
    button.style.display = "block";
    button.style.visibility = "visible";
    button.style.opacity = "1";
    
    // 應用手機版專用樣式
    applyMobileButtonStyles(button);
    
    // 清理舊的購物車高度設置
    cleanupOldCartSettings();
    
    // 額外保障：確保按鈕在 DOM 中且可見
    setTimeout(() => {
      if (button && isMobile()) {
        button.style.display = "block";
        button.style.visibility = "visible";
        button.style.opacity = "1";
        applyMobileButtonStyles(button);
      }
    }, 100);
  }
  
  initializeStickyCartListeners();
  addCartKeyboardListeners();
  addOutsideClickListeners();
  addTouchListeners();
  addScrollFollowListener();
  
  // 購物車高度調整功能已移除，僅保留向下拖動關閉功能
  
  // 初始化滾動增強功能
  setTimeout(() => {
    startScrollEnhancements();
  }, 200); // 稍微延遲以確保 DOM 完全準備好
}

// 自動初始化 - 優化重複調用
let isInitialized = false;

function performInitialization() {
  if (isInitialized) return;
  if (!document.querySelector(".sticky-cart-wrapper")) return;
  
  initializeStickyCart();
  isInitialized = true;
}

document.addEventListener('DOMContentLoaded', performInitialization);
window.addEventListener('load', performInitialization);

// 導出 API
window.StickyCart = {
  clearCart,
  addCartItem,
  updateCartSummary,
  updateCartDrawer,
  updateCartItemCounts,
  showCartLoading: () => toggleCartLoading(true),
  hideCartLoading: () => toggleCartLoading(false),
  initializeStickyCartListeners,
  initializeStickyCart,
  applyTabletButtonStyles,
  applyMobileButtonStyles,
  checkDeviceTypeChange,
  ensureButtonVisible,
  forceRefresh: () => {
    // 強制重新檢查和設置
    lastDeviceType = null;
    checkDeviceTypeChange();
    ensureButtonVisible();
  }
};

/**
 * 清理舊的購物車高度設置
 */
function cleanupOldCartSettings() {
  // 移除舊的高度設置
  localStorage.removeItem('sticky-cart-height');
  
  // 移除任何可能存在的 CSS 變數
  document.documentElement.style.removeProperty('--cart-height');
  
  // 重置購物車內聯樣式
  const { cart } = getElements();
  if (cart && isMobile()) {
    cart.style.height = '';
    cart.style.transition = '';
  }
}

// 購物車高度恢復功能已移除
// 現在手機版購物車使用固定的 CSS 高度設置

/**
 * 重置購物車高度到預設值
 */
// 多次點擊拖拽條調整高度功能已移除
// 現在手機版購物車只支援向下拖動關閉功能

/**
 * 滾动狀態檢測和視覺提示功能
 */
function initScrollEnhancements() {
  const cartItems = document.querySelector('.sticky-cart-items');
  if (!cartItems) {
    console.warn('Sticky cart items container not found. Scroll enhancements disabled.');
    return null;
  }

  // 檢查是否有滾動內容
  function checkScrollableContent() {
    const hasOverflow = cartItems.scrollHeight > cartItems.clientHeight;
    const itemCount = cartItems.querySelectorAll('.product-info').length;
    
    // 添加對應的 CSS 類別
    cartItems.classList.toggle('has-scroll', hasOverflow);
    cartItems.classList.toggle('has-many-items', itemCount > 3);
    
    return hasOverflow;
  }

  // 檢測滾動位置並添加相應的陰影效果
  function updateScrollShadows() {
    const scrollTop = cartItems.scrollTop;
    const scrollHeight = cartItems.scrollHeight;
    const clientHeight = cartItems.clientHeight;
    const scrollBottom = scrollHeight - scrollTop - clientHeight;
    
    // 根據滾動位置添加/移除 CSS 類別
    cartItems.classList.toggle('scroll-top', scrollTop > 10);
    cartItems.classList.toggle('scroll-bottom', scrollBottom > 10);
  }

  // 滾動中的視覺反饋（手機版）
  let scrollTimer;
  function handleScrollFeedback() {
    if (isMobile()) {
      cartItems.classList.add('scrolling');
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        cartItems.classList.remove('scrolling');
      }, 150);
    }
  }

  // 初始檢查
  setTimeout(() => {
    checkScrollableContent();
    updateScrollShadows();
  }, 100);

  // 監聽滾動事件
  cartItems.addEventListener('scroll', () => {
    updateScrollShadows();
    handleScrollFeedback();
  }, { passive: true });

  // 監聽內容變化（商品增減）
  const resizeObserver = new ResizeObserver(() => {
    setTimeout(() => {
      checkScrollableContent();
      updateScrollShadows();
    }, 50);
  });
  
  resizeObserver.observe(cartItems);

  // 監聽 DOM 變化（商品增減）
  const mutationObserver = new MutationObserver(() => {
    setTimeout(() => {
      checkScrollableContent();
      updateScrollShadows();
    }, 100);
  });
  
  mutationObserver.observe(cartItems, {
    childList: true,
    subtree: true
  });

  // 窗口大小變化時重新檢查
  const handleResize = () => {
    setTimeout(() => {
      checkScrollableContent();
      updateScrollShadows();
    }, 200);
  };
  
  window.addEventListener('resize', handleResize, { passive: true });

  return {
    checkScrollableContent,
    updateScrollShadows,
    destroy: () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    }
  };
}

/**
 * 平滑滾動到購物車中的特定商品
 */
function scrollToCartItem(productId) {
  const cartItems = document.querySelector('.sticky-cart-items');
  const targetItem = cartItems?.querySelector(`[data-product-id="${productId}"]`);
  
  if (targetItem && cartItems) {
    const itemTop = targetItem.offsetTop;
    const containerHeight = cartItems.clientHeight;
    const itemHeight = targetItem.offsetHeight;
    const scrollTop = itemTop - (containerHeight / 2) + (itemHeight / 2);
    
    cartItems.scrollTo({
      top: Math.max(0, scrollTop),
      behavior: 'smooth'
    });
  }
}

/**
 * 購物車滾動到頂部/底部的便利函數
 */
function scrollCartToTop() {
  const cartItems = document.querySelector('.sticky-cart-items');
  if (cartItems) {
    cartItems.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

function scrollCartToBottom() {
  const cartItems = document.querySelector('.sticky-cart-items');
  if (cartItems) {
    cartItems.scrollTo({ top: cartItems.scrollHeight, behavior: 'smooth' });
  }
}

// 全域滾动增強實例
let scrollEnhancementInstance = null;

// 在購物車初始化時啟動滾动增強功能
function startScrollEnhancements() {
  if (!document.querySelector('.sticky-cart-items')) return;

  if (scrollEnhancementInstance) {
    scrollEnhancementInstance.destroy();
    scrollEnhancementInstance = null;
  }
  
  // 嘗試多次初始化滾動增強功能，以防 DOM 還未完全準備好
  let attempts = 0;
  const maxAttempts = 5;
  
  function tryInitialize() {
    attempts++;
    const instance = initScrollEnhancements();
    
    if (instance) {
      scrollEnhancementInstance = instance;
      console.log('Scroll enhancements initialized successfully');
    } else if (attempts < maxAttempts) {
      console.log(`Scroll enhancements initialization attempt ${attempts} failed, retrying...`);
      setTimeout(tryInitialize, 200 * attempts); // 遞增延遲
    } else {
      console.warn('Failed to initialize scroll enhancements after multiple attempts');
    }
  }
  
  tryInitialize();
}

// 清理滾动增強功能
function stopScrollEnhancements() {
  if (scrollEnhancementInstance) {
    scrollEnhancementInstance.destroy();
    scrollEnhancementInstance = null;
  }
}

// 將滾動增強功能暴露給全域作用域以便外部調用
window.stickyCartScrollUtils = {
  scrollToItem: scrollToCartItem,
  scrollToTop: scrollCartToTop,
  scrollToBottom: scrollCartToBottom,
  initEnhancements: startScrollEnhancements,
  stopEnhancements: stopScrollEnhancements
};
